import { useEffect, useMemo, useState } from "react";
import { ArrowDownAZ, ArrowUpAZ, ClipboardCheck, AlertCircle, CheckCircle2, TrendingUp, Timer, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" });
  } catch { return iso; }
}

function gapColor(gap) {
  if (gap === null || gap === undefined) return "text-muted-foreground";
  if (gap <= 2) return "text-emerald-400";
  if (gap <= 5) return "text-amber-400";
  return "text-red-400";
}

const STATUS_BADGE = {
  PASS:      "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  UNCHECKED: "bg-amber-500/10 border-amber-500/30 text-amber-400",
};

export default function MoldCategoryCard({ accent = "#00D4FF", showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState({ rows: [], summary: {}, categories: [] });
  const [loading, setLoading] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("desc");
  const [search, setSearch] = useState("");

  // Categories dropdown needs the full list — fetch lightweight once on mount.
  useEffect(() => {
    api.get("/departments/MOLD/category-output", { params: { preset: "all" } })
      .then((r) => setData((d) => ({ ...d, categories: r.data.categories || [] })))
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = { preset: filter.preset, sort, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (status) p.status = status;
    if (category) p.category = category;
    return p;
  }, [filter, status, category, sort, showMexico]);

  useEffect(() => {
    if (!interacted) return;
    let cancelled = false;
    setLoading(true);
    api.get("/departments/MOLD/category-output", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, interacted]);

  const onFilterChange = (v) => { setInteracted(true); setFilter(v); };
  const onStatusChange = (v) => { setInteracted(true); setStatus(v); };
  const onCategoryChange = (v) => { setInteracted(true); setCategory(v); };
  const onSortToggle = () => { setInteracted(true); setSort((s) => (s === "desc" ? "asc" : "desc")); };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter((r) =>
      (r.job_num || "").toLowerCase().includes(q) ||
      (r.job_name || "").toLowerCase().includes(q) ||
      (r.piece_mark || "").toLowerCase().includes(q) ||
      (r.piece_sn || "").toLowerCase().includes(q)
    );
  }, [data.rows, search]);

  const summary = data.summary || {};
  const coverageDays = summary.coverage_days ?? 0;

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden"
      data-testid="mold-category-card"
    >
      <span
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}66 100%)` }}
      />

      {/* Header + Date Filter */}
      <div className="px-5 py-4 border-b border-border flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={18} style={{ color: accent }} />
            <h3 className="font-heading font-bold text-xl">Mold show</h3>
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              · QC Pipeline · {summary.total ?? 0} pieces
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="px-2 py-1 border border-border bg-[#0A0B10]">Coverage</span>
            <span
              className="px-2 py-1 text-[#0A0B10] font-mono font-semibold"
              style={{ backgroundColor: accent }}
              data-testid="coverage-badge"
            >
              {coverageDays}d
            </span>
          </div>
        </div>
        <DateFilter value={filter} onChange={onFilterChange} />
      </div>

      {/* Summary tiles — mirror the Patch Job Performance card
          (Total Pieces · Passed · Unchecked · Pass Rate · Total SF · Avg Gap · Max Gap). */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-px bg-border" data-testid="mold-summary">
        <Tile label="Total Pieces" value={interacted ? (summary.total ?? 0) : "—"} accent={accent} testId="tile-total" />
        <Tile label="Passed"       value={interacted ? (summary.passed ?? 0) : "—"} accent="#34D399" testId="tile-passed" icon={<CheckCircle2 size={12} />} />
        <Tile label="Unchecked"    value={interacted ? (summary.unchecked ?? 0) : "—"} accent="#FBBF24" testId="tile-unchecked" icon={<AlertCircle size={12} />} />
        <Tile label="Pass Rate"    value={interacted ? `${(summary.pass_rate ?? 0).toFixed(1)}%` : "—"} accent="#10B981" testId="tile-pass-rate" />
        <Tile label="Total SF"
              value={interacted ? Number(summary.total_sf ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—"}
              accent="#00D4FF" testId="tile-total-sf" />
        <Tile label="Avg Gap"      value={interacted ? fmtNum(summary.avg_gap_days ?? 0) : "—"} accent="#00D4FF" testId="tile-avg-gap" icon={<TrendingUp size={12} />} sub="days" />
        <Tile label="Max Gap"      value={interacted ? fmtNum(summary.max_gap_days ?? 0) : "—"} accent="#F87171" testId="tile-max-gap" icon={<Timer size={12} />} sub="days" />
      </div>

      {/* Filters bar */}
      <div className="px-5 py-3 border-b border-border flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="mold-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job / piece..."
            className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#00D4FF]"
          />
        </div>
        <select
          data-testid="mold-status-filter"
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#00D4FF]"
        >
          <option value="">All Status</option>
          <option value="PASS">PASS</option>
          <option value="UNCHECKED">UNCHECKED</option>
        </select>
        <select
          data-testid="mold-category-filter"
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#00D4FF] max-w-xs"
        >
          <option value="">All Categories</option>
          {data.categories?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          data-testid="mold-sort-toggle"
          onClick={onSortToggle}
          className="flex items-center gap-1.5 bg-[#0A0B10] border border-border px-3 py-1.5 text-xs uppercase tracking-widest hover:border-[#00D4FF]/60 transition-colors"
          title="Toggle sort direction"
        >
          {sort === "desc" ? <ArrowDownAZ size={12} /> : <ArrowUpAZ size={12} />}
          Gap {sort === "desc" ? "↓" : "↑"}
        </button>
      </div>

      {/* Empty state (before any interaction) */}
      {!interacted && (
        <div className="px-5 py-16 text-center" data-testid="mold-empty-state">
          <ClipboardCheck size={32} className="mx-auto text-muted-foreground/40 mb-3" />
          <div className="text-sm text-muted-foreground">Select a filter or date range to load pieces.</div>
          <div className="text-[10px] tracking-widest uppercase text-muted-foreground/60 mt-2">
            Use the date range, status, or category filters above
          </div>
          <button
            data-testid="mold-quick-30d"
            onClick={() => { setInteracted(true); setFilter({ preset: "30d" }); }}
            className="mt-5 inline-flex items-center gap-2 text-[#0A0B10] font-semibold text-xs uppercase tracking-widest px-4 py-2 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: accent }}
          >
            Quick load · Last 30 days →
          </button>
        </div>
      )}

      {/* Table */}
      {interacted && (
      <div className="overflow-x-auto" data-testid="mold-table-wrap">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="py-2.5 px-4">Job Num</th>
              <th className="py-2.5 px-4">Job Name</th>
              <th className="py-2.5 px-4">Piece Mark</th>
              <th className="py-2.5 px-4">Piece S/N</th>
              <th className="py-2.5 px-4">Cast Date</th>
              <th className="py-2.5 px-4">Check List</th>
              <th className="py-2.5 px-4 text-right">Gap Days</th>
              <th className="py-2.5 px-4">Category</th>
              <th className="py-2.5 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="py-12 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={9} className="py-12 text-center uppercase tracking-widest text-muted-foreground">No pieces match the filters</td></tr>
            )}
            {!loading && filteredRows.map((r, i) => (
              <tr key={`${r.job_num}-${r.piece_sn}-${i}`} className="border-t border-border/40 hover:bg-[#1A1C28]/40">
                <td className="py-2 px-4 font-mono" style={{ color: accent }}>{r.job_num || "—"}</td>
                <td className="py-2 px-4 max-w-[280px] truncate" title={r.job_name}>{r.job_name || "—"}</td>
                <td className="py-2 px-4 font-mono">{r.piece_mark || "—"}</td>
                <td className="py-2 px-4 font-mono text-muted-foreground">{r.piece_sn || "—"}</td>
                <td className="py-2 px-4 font-mono">{fmtDate(r.cast_date)}</td>
                <td className="py-2 px-4 font-mono">{fmtDate(r.check_list_date)}</td>
                <td className={`py-2 px-4 font-mono text-right tabular-nums font-semibold ${gapColor(r.gap_days)}`}
                    data-testid={`gap-${i}`}>
                  {r.gap_days === null || r.gap_days === undefined ? "—" : fmtNum(r.gap_days)}
                </td>
                <td className="py-2 px-4">
                  <span className="inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 bg-[#1F2230] border border-border text-[#CBD5E1] rounded-sm">
                    {r.category || "—"}
                  </span>
                </td>
                <td className="py-2 px-4">
                  <span className={`inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${STATUS_BADGE[r.status] || "border-border text-muted-foreground"}`}>
                    {r.status || "—"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {interacted && (
      <div className="px-5 py-2.5 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
        <span>Showing {filteredRows.length} of {data.rows?.length ?? 0}</span>
        <span>Sort: Gap Days {sort === "desc" ? "descending" : "ascending"}</span>
      </div>
      )}
    </section>
  );
}

function Tile({ label, value, accent, sub, testId, icon }) {
  return (
    <div data-testid={testId} className="bg-[#12141D] p-4 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        {icon && <span style={{ color: accent }}>{icon}</span>}
        {label}
      </div>
      <div className="font-heading font-bold text-2xl tabular-nums" style={{ color: accent }}>
        {fmtNum(value)}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
